import { formatFileSize, getExtensionFromFilename } from "@utils/fileUtils"

import { BLOCKED_FILE_TYPES } from "@constants/fileTypes"

export interface FileValidationResult {
    isValid: boolean
    error?: string
}

function normalizeAcceptedFileTypes(acceptedFileTypes: string[]): string[] {
    return acceptedFileTypes
        .flatMap((value) => value.split(","))
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
}

function isAcceptedFileType(file: File, acceptedFileTypes: string[]): boolean {
    const normalizedAccepted = normalizeAcceptedFileTypes(acceptedFileTypes)
    if (normalizedAccepted.length === 0) {
        return true
    }

    const mimeType = file.type.toLowerCase()
    const extension = getExtensionFromFilename(file.name)?.toLowerCase()

    return normalizedAccepted.some((accepted) => {
        if (accepted.startsWith(".")) {
            return extension ? accepted.slice(1) === extension : false
        }

        if (accepted.endsWith("/*")) {
            const prefix = accepted.slice(0, -1)
            return mimeType.startsWith(prefix)
        }

        return mimeType === accepted
    })
}

function isBlockedExtension(filename: string): boolean {
    const extension = getExtensionFromFilename(filename)
    if (!extension) {
        return false
    }
    return BLOCKED_FILE_TYPES.extensions.includes(extension as (typeof BLOCKED_FILE_TYPES.extensions)[number])
}

function isBlockedMimeType(mimeType: string): boolean {
    const normalizedMime = mimeType.toLowerCase()
    return BLOCKED_FILE_TYPES.mimeTypes.some(
        (blocked) => normalizedMime === blocked || normalizedMime.startsWith(blocked),
    )
}

/**
 * Validates a file against the security blocklist, optional accepted types, and size limit
 *
 * @param file - The File object to validate
 * @param maxSize - Max size in bytes
 * @param options.acceptedFileTypes - Optional accepted file types in HTML accept format (e.g. ".jpg", "image/*")
 * @returns FileValidationResult with isValid boolean and optional error message
 */
export function validateFile(
    file: File,
    maxSize: number,
    options?: {
        acceptedFileTypes?: string[]
    },
): FileValidationResult {
    // Check for blocked extensions first (highest priority security check)
    if (isBlockedExtension(file.name)) {
        const extension = getExtensionFromFilename(file.name)
        return {
            isValid: false,
            error: `File type ".${extension}" is not allowed for security reasons`,
        }
    }

    // Check for blocked MIME types
    if (isBlockedMimeType(file.type)) {
        return {
            isValid: false,
            error: `File type "${file.type}" is not allowed for security reasons`,
        }
    }

    // Validate against accepted file types
    if (options?.acceptedFileTypes && !isAcceptedFileType(file, options.acceptedFileTypes)) {
        const acceptedList = normalizeAcceptedFileTypes(options.acceptedFileTypes).join(", ")
        const extension = getExtensionFromFilename(file.name)
        const displayType = file.type || (extension ? `.${extension}` : "unknown")
        return {
            isValid: false,
            error: `File type "${displayType}" is not allowed. Accepted types: ${acceptedList}`,
        }
    }

    // Validate size
    if (file.size > maxSize) {
        return {
            isValid: false,
            error: `File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${formatFileSize(maxSize)}`,
        }
    }

    return { isValid: true }
}
